import { useSyncExternalStore } from 'react';

/** The three possible theme preference modes. */
export type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'theme-mode';

function getSystemIsDark(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyThemeAttribute(isDark: boolean): void {
    if (typeof document === 'undefined') return;
    if (isDark) {
        document.documentElement.setAttribute('data-theme', 'dark');
    } else {
        document.documentElement.removeAttribute('data-theme');
    }
}

function readStoredMode(): ThemeMode {
    if (typeof localStorage === 'undefined') return 'system';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
    }
    return 'system';
}

function resolveIsDark(mode: ThemeMode, systemIsDark: boolean): boolean {
    if (mode === 'dark') return true;
    if (mode === 'light') return false;
    return systemIsDark;
}

/**
 * Snapshot of the current theme state. The hook returns a stable object
 * whose identity only changes when one of its fields changes, so consumers
 * memoising on it (e.g. `useMemo(() => createAppTheme(isDark), [isDark])`
 * in `App`) only re-run when the value actually flips.
 */
interface ThemeSnapshot {
    themeMode: ThemeMode;
    isDark: boolean;
}

/**
 * Module-level theme store.
 *
 * The previous implementation kept theme state in component-local React
 * state, which meant calling `setThemeMode` from one component (e.g. the
 * `UserMenu`) only re-rendered that component — `App` never saw the change
 * so the MUI theme was never recreated, and every MUI-styled element kept
 * its old palette until the next page load (issue #1554).
 *
 * Instead, theme state lives in module scope. Every `useTheme()` instance
 * subscribes to the same store via `useSyncExternalStore`, so a single
 * `setThemeMode` call notifies every consumer in one tick.
 */
class ThemeStore {
    private mode: ThemeMode;

    private systemIsDark: boolean;

    private snapshot: ThemeSnapshot;

    private listeners = new Set<() => void>();

    private mediaQuery: MediaQueryList | null = null;

    private mediaListener: ((event: MediaQueryListEvent) => void) | null = null;

    constructor() {
        this.mode = readStoredMode();
        this.systemIsDark = getSystemIsDark();
        this.snapshot = this.computeSnapshot();
        applyThemeAttribute(this.snapshot.isDark);
        this.attachMediaListener();
    }

    /** Compute a fresh immutable snapshot from the current internal state. */
    private computeSnapshot(): ThemeSnapshot {
        const mode = this.mode;
        return {
            themeMode: mode,
            isDark: resolveIsDark(mode, this.systemIsDark),
        };
    }

    /**
     * Recompute the snapshot. If anything changed, replace the cached snapshot
     * (so React's reference-equality check in `useSyncExternalStore` fires)
     * and broadcast to subscribers.
     */
    private update(): void {
        const next = this.computeSnapshot();
        const prev = this.snapshot;
        if (prev.themeMode === next.themeMode && prev.isDark === next.isDark) {
            return;
        }
        this.snapshot = next;
        applyThemeAttribute(next.isDark);
        this.listeners.forEach((listener) => listener());
    }

    private attachMediaListener(): void {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return;
        }
        this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        this.mediaListener = (event: MediaQueryListEvent) => {
            this.systemIsDark = event.matches;
            this.update();
        };
        // Modern browsers; jsdom 22+ supports addEventListener.
        if (typeof this.mediaQuery.addEventListener === 'function') {
            this.mediaQuery.addEventListener('change', this.mediaListener);
        } else if (typeof (this.mediaQuery as MediaQueryList & { addListener?: (cb: (e: MediaQueryListEvent) => void) => void }).addListener === 'function') {
            (this.mediaQuery as MediaQueryList & { addListener: (cb: (e: MediaQueryListEvent) => void) => void }).addListener(this.mediaListener);
        }
    }

    subscribe(listener: () => void): () => void {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }

    getSnapshot(): ThemeSnapshot {
        return this.snapshot;
    }

    setMode(mode: ThemeMode): void {
        if (typeof localStorage !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, mode);
        }
        this.mode = mode;
        this.update();
    }

    /**
     * Test-only: reset the singleton's state from disk + system preference.
     * Production code never calls this; tests that mutate `localStorage` and
     * `window.matchMedia` between cases use it to start each case clean.
     */
    resetForTests(): void {
        this.mode = readStoredMode();
        this.systemIsDark = getSystemIsDark();
        const next = this.computeSnapshot();
        this.snapshot = next;
        applyThemeAttribute(next.isDark);
        // Re-attach the media listener against whatever fresh matchMedia mock
        // the current test installed.
        if (this.mediaQuery && this.mediaListener) {
            if (typeof this.mediaQuery.removeEventListener === 'function') {
                this.mediaQuery.removeEventListener('change', this.mediaListener);
            } else if (
                typeof (this.mediaQuery as MediaQueryList & { removeListener?: (cb: (e: MediaQueryListEvent) => void) => void }).removeListener === 'function'
            ) {
                (this.mediaQuery as MediaQueryList & { removeListener: (cb: (e: MediaQueryListEvent) => void) => void }).removeListener(this.mediaListener);
            }
        }
        this.mediaQuery = null;
        this.mediaListener = null;
        this.attachMediaListener();
        // Tell any mounted subscribers that the snapshot identity has been
        // refreshed so they re-render against the new state.
        this.listeners.forEach((listener) => listener());
    }
}

const themeStore = new ThemeStore();

/**
 * Test-only helper. Re-reads `localStorage` + `matchMedia` and broadcasts
 * to every active subscriber. Exported for the unit-test file; do not call
 * from production code.
 */
export function __resetThemeStoreForTests(): void {
    themeStore.resetForTests();
}

/** Return value from {@link useTheme}. */
export interface UseThemeReturn {
    /** The currently selected theme mode preference. */
    themeMode: ThemeMode;
    /** Whether dark mode is currently active (accounting for system preference). */
    isDark: boolean;
    /** Update the theme mode preference. Persisted to localStorage. */
    setThemeMode: (mode: ThemeMode) => void;
}

/**
 * Hook that exposes the application theme mode.
 *
 * Reads from `localStorage` key `theme-mode` on first load and defaults to
 * `'system'`. Listens to `prefers-color-scheme` media query changes when
 * mode is `'system'`. Applies `data-theme="dark"` to `document.documentElement`
 * when dark mode is active.
 *
 * State is stored in a module-level singleton so every consumer of this hook
 * stays in sync — `setThemeMode` from one component re-renders every other
 * component that calls `useTheme()` in the same tick. This guarantees that
 * the `App`-level MUI theme is recreated whenever the mode changes, even if
 * the toggle lives deep in a different subtree (issue #1554).
 */
export function useTheme(): UseThemeReturn {
    const snapshot = useSyncExternalStore(
        (listener) => themeStore.subscribe(listener),
        () => themeStore.getSnapshot(),
        () => themeStore.getSnapshot()
    );

    return {
        themeMode: snapshot.themeMode,
        isDark: snapshot.isDark,
        setThemeMode: (mode) => themeStore.setMode(mode),
    };
}
